import React from 'react';
import classNames from 'classnames';
import convertUTCToTimezone from '@betgames/bg-tools/dist/utils/convertUTCToTimezone';
import { ITournament } from 'twain-sport-fe-mono/interfaces/ITournament';
import { TournamentStatus, NotificationTypes } from 'twain-sport-fe-mono/enums';
import { formatDate } from 'twain-sport-fe-mono/utils';
import { EMPTY_SPACE } from 'twain-sport-fe-mono/constants/textSymbols';
import { translationProvider } from 'twain-sport-fe-mono/store/translations';
import { Notification } from '../Notification';
import classes from './TournamentInfo.module.scss';

interface IProps {
    tournament: Partial<ITournament>;
    timezone: number;
    className?: string;
    timeFormat: string;
    renderTitle?(title: string): React.ReactNode;
}

const defaultProps: Partial<IProps> = {
    renderTitle: (title: string) => <h3 className={classes.title}>{title}</h3>,
};

export const TournamentInfo: React.FC<React.PropsWithChildren<IProps>> = (props) => {
    const renderTextByStatus = React.useMemo((): React.ReactNode => {
        let text = '';

        if (props.tournament.status === TournamentStatus.FINISHED) {
            return '';
        }
        if (props.tournament.status === TournamentStatus.CANCELED) {
            return (
                <Notification
                    className={classes.notification}
                    type={NotificationTypes.ERROR}
                    message={`${translationProvider.string(
                        'tournament_was_canceled',
                    )} (${translationProvider.string(
                        `reason_${props.tournament.cancellationReason?.toLowerCase()}`,
                    )})`}
                />
            );
        }
        if (props.tournament.status === TournamentStatus.OPENED) {
            text = translationProvider.string('betting_is_opened');
        } else {
            text = `${translationProvider.string('betting_will_open')} ${formatDate(
                convertUTCToTimezone(props.tournament.bettingStartsAt, props.timezone),
                props.timeFormat,
            )}`;
        }

        return `(${text})`;
    }, [props.tournament.status, props.tournament.bettingStartsAt]);

    const renderStartText = React.useMemo((): string => {
        const time = formatDate(
            convertUTCToTimezone(props.tournament.startsAt, props.timezone),
            props.timeFormat,
        );

        if (props.tournament.startsAt < Date.now()) {
            return `${translationProvider.string('tournament_started_at')} ${time}`;
        }

        return `${translationProvider.string('tournament_starts_at')} ${time}`;
    }, [props.tournament.startsAt]);

    return (
        <div className={classNames(classes.container, props.className)}>
            <div className={classes.head}>
                {props.renderTitle(props.tournament.name)}
                {!!props.children && <div className={classes.action}>{props.children}</div>}
            </div>
            <div className={classes.details}>
                {props.tournament.status !== TournamentStatus.CANCELED && (
                    <span>
                        {renderStartText}
                        {EMPTY_SPACE}
                    </span>
                )}
                {!!props.tournament.status && (
                    <span
                        className={classNames(classes.status, {
                            [classes.open]: props.tournament.status === TournamentStatus.OPENED,
                        })}
                    >
                        {renderTextByStatus}
                    </span>
                )}
            </div>
        </div>
    );
};

TournamentInfo.defaultProps = defaultProps;
